<template>
  <StaffLayout :section="`Notifications (${totalUnresolvedCount})`" showHeader>
    <BaseButton @click="refreshNotifications(true)">Refresh</BaseButton>
    <div class="float-right mr-4">
      Page
      <input
        class="inline w-16 h-8 mx-2 text-center border"
        type="number"
        :min="1"
        :max="totalPages"
        v-model.lazy="pageNumber"
      />
      of
      {{ totalPages }}
    </div>
    <div class="mt-4">
      <form v-on:submit.prevent>
        <input
          v-model.lazy="searchString"
          placeholder="Search notifications"
          class="shadow appearance-none border rounded py-1 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
        />
        <SearchButton @click="searchNotifications()" type="submit"> Search </SearchButton>
      </form>
    </div>
    <div class="mt-4">
      <BaseButton
        :class="{ invisible: selectedNotificationIds.length == 0 }"
        class="mr-4"
        @click="openResolutionPopup()"
      >
        Resolve selected ({{ selectedNotificationIds.length }})
      </BaseButton>
      <BaseButton
        :class="{ invisible: selectedNotificationIds.length == 0 }"
        @click="selectedNotificationIds = []"
      >
        Clear selection
      </BaseButton>
    </div>
    <Loading v-if="isLoading" />
    <div v-else>
      <div v-if="notifications?.length > 0">
        <div v-if="unresolvedNotifications?.length > 0" class="font-bold text-xl mt-4">
          {{ totalUnresolvedCount }} Unresolved
        </div>
        <div
          v-for="notif in unresolvedNotifications"
          :key="notif.staffNotificationId"
          class="flex border my-2"
        >
          <input
            type="checkbox"
            class="w-4 h-4 m-auto mx-4"
            :value="notif.staffNotificationId"
            v-model="selectedNotificationIds"
          />
          <div
            class="border-t p-4 px-6 bg-gray-200 hover:bg-blue-200 cursor-pointer flex-grow"
            @click="openResolutionPopup(notif.staffNotificationId)"
          >
            <p class="text-xl font-medium line-clamp-1">{{ notif.title }}</p>
            <div class="flex mt-2">
              <div class="flex-grow">
                {{ formatDateTime(notif.timeStamp) }}
              </div>
              <div class="text-xl">Resolve</div>
            </div>
          </div>
        </div>
        <div
          v-if="resolvedNotifications?.length > 0"
          class="text-xl mt-4 hover:underline cursor-pointer"
          @click="showResolved = !showResolved"
        >
          Resolved Notifications ({{ showResolved ? "hide" : "show" }})
        </div>
        <div v-if="showResolved">
          <div
            v-for="notif in resolvedNotifications"
            :key="notif.staffNotificationId"
            class="flex border-t my-2 p-4 px-6 bg-gray-100"
          >
            <div class="my-auto w-28 md:w-32 xl:w-28">
              <IHeroiconsOutlineCheck class="text-green-800 h-12 w-20" />
            </div>
            <div class="w-full">
              <div class="pr-4 grow">
                <div class="text-xl">{{ notif.title }}</div>
                <div>{{ notif.description }}</div>
                <div class="mt-2">
                  {{ formatDateTime(notif.timeStamp) }}
                </div>
              </div>
              <div class="mt-8">
                <div>
                  {{
                    `Resolved  by ${notif.resolvedByPersonFirstName} ${notif.resolvedByPersonLastName} (${notif.resolvedByPersonLoginEmail})`
                  }}
                </div>
                <div>
                  {{ `${formatDateTime(notif.timeResolved)}` }}
                </div>
              </div>
              <div v-if="notif.resolvedComment?.length > 0" class="font-medium">
                Comment: {{ notif.resolvedComment }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="mt-8">No notifications found.</div>
    </div>

    <BasePopup
      title="Resolve Notification"
      :is-visible="isPopupVisible"
      @close="isPopupVisible = false"
    >
      <ErrorDisplay
        label="An error occurred"
        :message="popupError"
        :isVisible="popupError != null"
      />
      <div class="mb-2 text-xl">
        {{ selectedTitle }}
      </div>
      <div class="mb-8">
        {{ selectedDescription }}
      </div>
      <BaseTextarea
        class="h-64 mb-16"
        label="Comments (optional)"
        v-model="resolvedComment"
        :allowResize="false"
        :error="errors.resolvedComment"
      />
      <div class="flex">
        <span class="flex-grow" />
        <BaseButton @click="resolveNotification">Resolve</BaseButton>
      </div>
    </BasePopup>
  </StaffLayout>
</template>

<script setup lang="ts">
import { useAdminStore } from "../../store/adminStore";
import { useUserStore } from "../../store/userStore";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import {
  GetStaffNotificationsRequest,
  ResolveStaffNotificationsRequest,
} from "../../store/generatedFetchClient";

const userStore = useUserStore();
const adminStore = useAdminStore();
const isLoading = ref(false);
const notifications = computed(() => adminStore.staffNotificationVm?.staffNotifications);
const resolvedNotifications = computed(() =>
  notifications.value
    ?.filter((n) => n.timeResolved)
    .sort((a, b) => (a.timeResolved > b.timeResolved ? -1 : 1))
);
const unresolvedNotifications = computed(() =>
  notifications.value?.filter((n) => !n.timeResolved)
);
const showResolved = ref(false);

onMounted(loadData);
async function loadData() {
  refreshNotifications();
  searchString.value = adminStore.staffNotificationSearchString;
}

async function refreshNotifications(force: boolean = false) {
  isLoading.value = true;
  await adminStore.getStaffNotifications(
    {
      pageNumber: pageNumber.value,
      searchString: searchString.value,
    } as GetStaffNotificationsRequest,
    force
  );
  await adminStore.getCounts(force);

  isLoading.value = false;
}

const popupError = ref("");
const isPopupVisible = ref(false);
var selectedNotificationIds = ref([] as number[]);
const selectedTitle = computed(() =>
  selectedNotificationIds.value.length == 1
    ? unresolvedNotifications.value.find(
        (n) => n.staffNotificationId == selectedNotificationIds.value[0]
      )?.title
    : `${selectedNotificationIds.value.length} Selected`
);
const selectedDescription = computed(() =>
  selectedNotificationIds.value.length == 1
    ? unresolvedNotifications.value.find(
        (n) => n.staffNotificationId == selectedNotificationIds.value[0]
      )?.description
    : ""
);

const { handleSubmit, errors } = useForm({
  validationSchema: yup.object({
    resolvedComment: yup.string().nullable().notRequired().label("Comments"),
  }),
  initialValues: {
    resolvedComment: null,
  },
});

const { value: resolvedComment } = useField<string>("resolvedComment");

function openResolutionPopup(notificationId?: number) {
  resolvedComment.value = null;
  if (notificationId) selectedNotificationIds.value = [notificationId];

  isPopupVisible.value = true;
}

let resolveNotification = handleSubmit(async () => {
  let request = {
    staffNotificationIds: selectedNotificationIds.value,
    resolvedByPersonId: userStore.user.personId,
    resolvedComment: resolvedComment.value?.length === 0 ? null : resolvedComment.value,
  } as ResolveStaffNotificationsRequest;

  popupError.value = "";
  popupError.value = await adminStore.resolveStaffNotifications(request);
  if (popupError.value) return;

  refreshNotifications(true);
  isPopupVisible.value = false;
  selectedNotificationIds.value = [];
}, onInvalidSubmit);

function onInvalidSubmit() {
  popupError.value = "One or more invalid form inputs.";
}

// #region Pagination

const pageNumber = ref(1);
watch(pageNumber, () => {
  if (pageNumber.value > totalPages.value) pageNumber.value = totalPages.value;
  else if (pageNumber.value <= 0) pageNumber.value = 1;
  refreshNotifications(true);
});
const totalPages = computed(() => adminStore.staffNotificationVm?.totalPages ?? 1);
const totalUnresolvedCount = computed(
  () => adminStore.staffNotificationVm?.totalUnresolvedCount ?? 0
);

// #endregion Pagination

// #region Filter string

const searchString = ref("");
async function searchNotifications() {
  isLoading.value = true;
  await adminStore.getStaffNotifications(
    {
      pageNumber: pageNumber.value,
      searchString: searchString.value,
    } as GetStaffNotificationsRequest,
    true
  );
  adminStore.staffNotificationSearchString = searchString.value;
  pageNumber.value = 1;
  isLoading.value = false;
}

// #endregion Filter string

// #region Helpers

function formatDateTime(date: any) {
  let d = new Date(date);
  let formatedDate = d.toDateString() + " " + d.toLocaleTimeString();
  return formatedDate === "Invalid Date" ? "" : formatedDate;
}

// #endregion Helpers
</script>
